import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Typography,
  Tag,
  Button,
  Space,
  Modal,
  Input,
  Descriptions,
  Alert,
  Spin,
  Table,
  Row,
  Col,
  Badge,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
  UserOutlined,
  FileTextOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
  SendOutlined,
} from "@ant-design/icons";
import {
  useGetProposalByIdQuery,
  useGetProposalStatusHistoryQuery,
  useSendProposalMutation,
  useAcceptProposalMutation,
  useHoldProposalMutation,
  useExpireProposalMutation,
  useConvertProposalMutation,
  useDuplicateProposalMutation,
  useCancelSendProposalMutation,
  useUpdateItemImageDescriptionMutation,
  useDeleteItemImageMutation,
  useReorderItemImagesMutation,
  useReorderProposalItemsMutation,
  useSaveProposalPdfOptionsMutation,
  proposalsApi,
} from "../../redux/api/proposalsApi";
import { useGetProposalStatusesQuery } from "../../redux/api/referencesApi";
import { useGetLanguagesQuery } from "../../redux/api/languagesApi";
import axios from "../../services/axiosInstance";
import { API_BASE_URL } from "../../config/environment";
import { toast } from "react-toastify";
import StatusBadge from "../../components/StatusBadge";
import PDFTemplateOptions, {
  getMaxImagesForLayout,
} from "./PDFTemplateOptions";
import CleanProposalPDFOptions from "./CleanProposalPDFOptions";
import {
  convertAndFormatPrice,
  getCurrencySymbol,
} from "../../utils/currencyUtils";
import {
  savePdfOptions,
  loadPdfOptions,
  hasSavedPdfOptions,
  ensureCustomSectionIds,
  cloneCustomSections,
} from "../../utils/pdfOptionsPersistence";
import PdfSnapshotsTable from "../../components/PdfSnapshotsTable";
import PieceGrid from "../../components/PieceGrid";
import PDFGenerationLoader from "../../components/PDFGenerationLoader";
import ProposalPreview from "../../components/ProposalPreview";
import EntityNotes from "../../components/EntityNotes";
import CustomerAutocomplete from "../../components/CustomerAutocomplete";
import "../../components/PieceGrid.css";

const { Title, Text } = Typography;

const DEFAULT_PDF_OPTIONS = {
  // Image size option removed from UI; always use large
  imageSize: "large",
  showCustomerInfo: true,
  hidePriceSummary: false,
  showLineTotalWithVat: false,
  showDiscountPercentage: false,
  stripVat: false,
  selectedImages: {},
  selectedProductSummaryImages: {},
  customSections: [],
  additionalInformation: "",
};

const ProposalDetailsPage = () => {
  const getDefaultValidUntil = () =>
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0];

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // RTK Query hooks
  const {
    data: proposal,
    isLoading: loading,
    error,
    refetch,
  } = useGetProposalByIdQuery(id);
  const {
    data: rawStatusOptions = [],
    isLoading: statusLoading,
    error: statusError,
  } = useGetProposalStatusesQuery();
  const { refetch: refetchStatusHistory } = useGetProposalStatusHistoryQuery(id);
  const { data: languages = [] } = useGetLanguagesQuery();
  const [saveProposalPdfOptions] = useSaveProposalPdfOptionsMutation();

  // Mutations
  const [sendProposal] = useSendProposalMutation();
  const [acceptProposal] = useAcceptProposalMutation();
  const [holdProposal] = useHoldProposalMutation();
  const [expireProposal] = useExpireProposalMutation();
  const [convertProposal] = useConvertProposalMutation();
  const [duplicateProposal, { isLoading: isDuplicating }] =
    useDuplicateProposalMutation();
  const [cancelSendProposal] = useCancelSendProposalMutation();
  const [updateItemImageDescription] = useUpdateItemImageDescriptionMutation();
  const itemImageUploadQueueRef = useRef({});
  const customSectionUploadQueueRef = useRef({});
  const [deleteItemImage] = useDeleteItemImageMutation();
  const [reorderItemImages] = useReorderItemImagesMutation();
  const [reorderProposalItems] = useReorderProposalItemsMutation();

  // Map status options
  const statusOptions = rawStatusOptions.map((s) => ({
    id: s.proposal_status_id,
    name: s.status_name,
  }));

  // Get debug settings from Redux store
  const debugFeatures = useSelector((state) => state.app?.debugFeatures);

  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("product_summary");

  const [pdfOptions, setPdfOptions] = useState(DEFAULT_PDF_OPTIONS);
  const pdfOptionsDirtyRef = React.useRef(false);
  const pdfOptionsRef = React.useRef(pdfOptions);
  const selectedTemplateRef = React.useRef(selectedTemplate);
  const skipServerPersistRef = React.useRef(true);
  const saveTimeoutRef = React.useRef(null);
  const appliedServerKeyRef = React.useRef("");
  pdfOptionsRef.current = pdfOptions;
  selectedTemplateRef.current = selectedTemplate;
  const [editingImageId, setEditingImageId] = useState(null);
  const [editingDescription, setEditingDescription] = useState("");
  const [uploadingImages, setUploadingImages] = useState({});
  const [draggedImage, setDraggedImage] = useState(null);
  const [itemsDragOverIndex, setItemsDragOverIndex] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfTimeoutId, setPdfTimeoutId] = useState(null);
  const [uploadingCustomSectionImages, setUploadingCustomSectionImages] =
    useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [refreshPreview, setRefreshPreview] = useState(() => () => {});
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateCustomerId, setDuplicateCustomerId] = useState(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicateValidUntil, setDuplicateValidUntil] = useState(
    getDefaultValidUntil()
  );
  const duplicateSubmitLockRef = useRef(false);

  const templateOptions = [
    { value: "product_summary", label: "Product Summary Proposal" },
    { value: "pretty_proposal", label: "Pretty Proposal" },
  ];
  // Image size option removed from UI; generator uses large by default

  const persistPdfOptionsToServer = (proposalId, templateType, options) => {
    if (!proposalId) return Promise.resolve();
    return saveProposalPdfOptions({
      proposalId,
      templateType,
      options,
    })
      .unwrap()
      .then(() => {
        pdfOptionsDirtyRef.current = false;
      })
      .catch(() => {});
  };

  // Load PDF options from localStorage immediately (per-browser cache)
  useEffect(() => {
    if (id) {
      pdfOptionsDirtyRef.current = false;
      skipServerPersistRef.current = true;
      appliedServerKeyRef.current = "";
      const savedOptions = loadPdfOptions(
        id,
        selectedTemplate,
        DEFAULT_PDF_OPTIONS
      );
      setPdfOptions(
        ensureCustomSectionIds({
          ...DEFAULT_PDF_OPTIONS,
          ...savedOptions,
        })
      );
    }
  }, [id, selectedTemplate]);

  // Overlay server-persisted options when the proposal loads
  useEffect(() => {
    if (!id || !proposal) return;
    const serverMap =
      proposal.pdf_options && typeof proposal.pdf_options === "object"
        ? proposal.pdf_options
        : {};
    const applyKey = `${id}:${selectedTemplate}:${JSON.stringify(serverMap)}`;
    if (appliedServerKeyRef.current === applyKey) return;
    appliedServerKeyRef.current = applyKey;

    const serverOpts = serverMap[selectedTemplate];
    if (serverOpts && typeof serverOpts === "object" && Object.keys(serverOpts).length > 0) {
      skipServerPersistRef.current = true;
      const merged = ensureCustomSectionIds({
        ...DEFAULT_PDF_OPTIONS,
        ...serverOpts,
      });
      setPdfOptions(merged);
      savePdfOptions(id, selectedTemplate, merged);
      pdfOptionsDirtyRef.current = false;
      return;
    }

    if (hasSavedPdfOptions(id, selectedTemplate)) {
      const localOptions = ensureCustomSectionIds({
        ...DEFAULT_PDF_OPTIONS,
        ...loadPdfOptions(id, selectedTemplate, {}),
      });
      pdfOptionsDirtyRef.current = true;
      persistPdfOptionsToServer(id, selectedTemplate, localOptions);
    }
  }, [id, selectedTemplate, proposal]);

  // Cache to localStorage always; debounce persist to the server after hydration
  useEffect(() => {
    if (!id) return;
    savePdfOptions(id, selectedTemplate, pdfOptions);
    if (skipServerPersistRef.current) {
      skipServerPersistRef.current = false;
      return;
    }
    pdfOptionsDirtyRef.current = true;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      persistPdfOptionsToServer(id, selectedTemplate, pdfOptions);
    }, 800);
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [id, selectedTemplate, pdfOptions]);

  // Flush pending PDF options to the server when leaving the page
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (id && pdfOptionsDirtyRef.current) {
        persistPdfOptionsToServer(
          id,
          selectedTemplateRef.current,
          pdfOptionsRef.current
        );
      }
    };
  }, [id, saveProposalPdfOptions]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (pdfTimeoutId) {
        clearTimeout(pdfTimeoutId);
      }
    };
  }, [pdfTimeoutId]);

  // Update options when template changes or proposal loads
  useEffect(() => {
    if (selectedTemplate === "product_summary") {
      setPdfOptions((opts) => ({
        ...opts,
        // Force large images for product summary template
        imageSize: "large",
        selectedProductSummaryImages: opts.selectedProductSummaryImages || {},
      }));
    } else if (
      selectedTemplate === "image_proposal" ||
      selectedTemplate === "upgraded_image_proposal"
    ) {
      setPdfOptions((opts) => ({
        ...opts,
        showCustomerInfo: opts.showCustomerInfo !== false,
        selectedImages: opts.selectedImages || {},
      }));
    }
  }, [selectedTemplate, proposal]);

  useEffect(() => {
    if (
      proposal &&
      (selectedTemplate === "image_proposal" ||
        selectedTemplate === "upgraded_image_proposal")
    ) {
      setPdfOptions((opts) => {
        const newSelectedImages = { ...opts.selectedImages };
        proposal.items?.forEach((item) => {
          if (
            !Array.isArray(newSelectedImages[item.proposal_item_id]) ||
            newSelectedImages[item.proposal_item_id].length === 0
          ) {
            newSelectedImages[item.proposal_item_id] = (
              item.all_images || []
            ).map((img) => img.image_id);
          }
        });
        return { ...opts, selectedImages: newSelectedImages };
      });
    }
  }, [proposal, selectedTemplate]);

  useEffect(() => {
    if (proposal && selectedTemplate === "product_summary") {
      setPdfOptions((opts) => {
        const newSelectedProductSummaryImages = {
          ...opts.selectedProductSummaryImages,
        };
        proposal.items?.forEach((item) => {
          if (
            !newSelectedProductSummaryImages[item.proposal_item_id] &&
            item.all_images &&
            item.all_images.length > 0
          ) {
            newSelectedProductSummaryImages[item.proposal_item_id] =
              item.all_images[0].image_id;
          }
        });
        return {
          ...opts,
          selectedProductSummaryImages: newSelectedProductSummaryImages,
        };
      });
    }
  }, [proposal, selectedTemplate]);

  const handleDownloadPDF = async () => {
    console.log(
      "Requesting PDF download for proposal:",
      id,
      "with template:",
      selectedTemplate,
      "and options:",
      pdfOptions
    );
    setIsGeneratingPDF(true);

    // Clear any existing timeout
    if (pdfTimeoutId) {
      clearTimeout(pdfTimeoutId);
    }

    // Set a timeout to prevent infinite loading (5 minutes)
    const timeoutId = setTimeout(() => {
      setIsGeneratingPDF(false);
      toast.error("PDF generation timed out. Please try again.");
    }, 300000);

    setPdfTimeoutId(timeoutId);

    try {
      await persistPdfOptionsToServer(id, selectedTemplate, pdfOptions);

      const response = await axios.post(
        `/proposals/${id}/generate-pdf`,
        {
          type: selectedTemplate,
          options: pdfOptions,
        },
        { timeout: 300000 }
      );

      // Backend returns JSON with pdf_url (legacy inline) or file_path (snapshot URL)
      const { type, pdf_url, file_path } = response.data;

      setIsGeneratingPDF(false);
      clearTimeout(timeoutId);
      setPdfTimeoutId(null);

      if (type === "inline" && pdf_url) {
        // Browsers block window.open(data:...) and show about:blank#blocked.
        // Convert data URL to a blob URL so the new tab can open.
        const binary = atob(pdf_url.split(",")[1]);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank", "noopener,noreferrer");
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } else if (type === "download" && file_path) {
        const baseUrl = API_BASE_URL.replace(/\/+$/, "").replace(
          /\/api\/?$/,
          ""
        );
        const cleanPath = file_path.replace(/^\/+/, "");
        const token = localStorage.getItem("token");
        const separator = cleanPath.includes("?") ? "&" : "?";
        const authenticatedPath =
          token && cleanPath.startsWith("api/")
            ? `${cleanPath}${separator}token=${encodeURIComponent(token)}`
            : cleanPath;
        const urlToOpen = `${baseUrl}/${authenticatedPath}`;
        window.open(urlToOpen, "_blank", "noopener,noreferrer");
      } else {
        throw new Error("Invalid PDF response format");
      }

      // Invalidate PDF snapshot list so version history updates without refresh
      dispatch(proposalsApi.util.invalidateTags([{ type: "PdfSnapshot", id }]));
    } catch (err) {
      console.error("PDF generation failed:", err);
      const timedOut = err?.code === "ECONNABORTED";
      toast.error(
        timedOut
          ? "PDF generation timed out. Please try again."
          : err?.response?.data?.error || "PDF generation failed. Please try again."
      );
      setIsGeneratingPDF(false);
      clearTimeout(timeoutId);
      setPdfTimeoutId(null);
    }
  };

  const openDuplicateModal = () => {
    const rawOwnerId =
      proposal?.prospect_id ?? proposal?.customer?.person_id ?? null;
    const ownerPersonId =
      rawOwnerId != null && rawOwnerId !== ""
        ? Number(rawOwnerId)
        : null;
    setDuplicateCustomerId(
      ownerPersonId != null && !Number.isNaN(ownerPersonId)
        ? ownerPersonId
        : null
    );
    setDuplicateName(
      proposal?.name ? `${proposal.name} (copy)` : "Duplicated proposal"
    );
    setDuplicateValidUntil(getDefaultValidUntil());
    setIsDuplicateModalOpen(true);
  };

  const handleDuplicateProposal = async () => {
    if (!duplicateCustomerId) {
      toast.error("Please select a customer for the duplicated proposal.");
      return;
    }
    if (duplicateSubmitLockRef.current) {
      return;
    }
    duplicateSubmitLockRef.current = true;

    try {
      const result = await duplicateProposal({
        proposalId: id,
        prospect_id: duplicateCustomerId,
        name: duplicateName.trim(),
        valid_until: duplicateValidUntil,
      }).unwrap();

      const newProposalId =
        result?.proposal_id ?? result?.data?.proposal_id ?? null;
      if (newProposalId == null || newProposalId === "") {
        toast.error(
          "Duplication response did not include a proposal id. Check the proposals list or try again."
        );
        return;
      }

      setIsDuplicateModalOpen(false);
      navigate(`/proposals/${newProposalId}/edit`);
      toast.success("Proposal duplicated successfully.");
    } catch (err) {
      toast.error(err?.data?.error || "Failed to duplicate proposal");
    } finally {
      duplicateSubmitLockRef.current = false;
    }
  };

  // Helper: get current status object
  const currentStatus = proposal
    ? statusOptions.find((s) => s.id === proposal.proposal_status_id)
    : null;

  // Helper: check if customer has delivery address
  const hasDeliveryAddress = () => {
    if (!proposal?.customer?.addresses) return false;
    return proposal.customer.addresses.some(
      (address) =>
        address.address_type_id === 2 ||
        address.address_type_name === "Delivery"
    );
  };

  // Helper: get next valid status transitions and their backend endpoints
  const getNextActions = () => {
    if (!currentStatus) return [];
    switch (currentStatus.name) {
      case "Draft":
        return [{ name: "Send to Customer", endpoint: "send", btn: "primary" }];
      case "Under Negotiation":
        const acceptAction = hasDeliveryAddress()
          ? { name: "Accept", endpoint: "accept", btn: "success" }
          : {
              name: "Accept (No Delivery Address)",
              endpoint: "accept",
              btn: "secondary",
              disabled: true,
            };
        return [
          acceptAction,
          {
            name: "Cancel Send",
            endpoint: "cancel-send",
            btn: "outline-warning",
          },
          { name: "Hold", endpoint: "hold", btn: "warning" },
          { name: "Expire", endpoint: "expire", btn: "secondary" },
        ];
      case "Accepted":
        const convertAction = hasDeliveryAddress()
          ? { name: "Convert to Order", endpoint: "convert", btn: "primary" }
          : {
              name: "Convert to Order (No Delivery Address)",
              endpoint: "convert",
              btn: "secondary",
              disabled: true,
            };
        return [
          convertAction,
          { name: "Hold", endpoint: "hold", btn: "warning" },
        ];
      case "On Hold":
        return [
          { name: "Accept", endpoint: "accept", btn: "success" },
          { name: "Expire", endpoint: "expire", btn: "secondary" },
        ];
      case "Expired":
        return [];
      case "Converted to Order":
        return [];
      case "Rejected":
        return [];
      default:
        return [];
    }
  };

  // Handle status action button click
  const handleStatusAction = async (endpoint) => {
    // Prevent accept/convert if no delivery address
    if (
      (endpoint === "accept" || endpoint === "convert") &&
      !hasDeliveryAddress()
    ) {
      toast.error(
        "Customer must have a delivery address to accept proposal and convert to order"
      );
      return;
    }

    // Confirm cancel send action
    if (endpoint === "cancel-send") {
      if (
        !window.confirm(
          "Are you sure you want to cancel sending this proposal? This will revert it back to draft status."
        )
      ) {
        return;
      }
    }

    setUpdatingStatus(true);
    try {
      const proposalId = proposal.proposal_id;

      switch (endpoint) {
        case "send":
          await sendProposal(proposalId).unwrap();
          toast.success("Proposal sent to customer");
          break;
        case "accept":
          await acceptProposal(proposalId).unwrap();
          await convertProposal(proposalId).unwrap();
          toast.success("Proposal accepted and converted to order");
          break;
        case "hold":
          await holdProposal(proposalId).unwrap();
          toast.success("Proposal put on hold");
          break;
        case "expire":
          await expireProposal(proposalId).unwrap();
          toast.success("Proposal marked as expired");
          break;
        case "convert":
          await convertProposal(proposalId).unwrap();
          toast.success("Proposal converted to order");
          break;
        case "cancel-send":
          await cancelSendProposal(proposalId).unwrap();
          toast.success("Proposal reverted to draft status");
          break;
        default:
          toast.error("Unknown action");
      }

      refetch();
      refetchStatusHistory();
    } catch (err) {
      if (err?.data?.error) {
        toast.error(err.data.error);
      } else {
        toast.error("Failed to update proposal status");
      }
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Helper to display language name
  const getLanguageName = (languageId) => {
    const lang = languages.find(
      (l) => String(l.language_id) === String(languageId)
    );
    return lang ? lang.name : `Language #${languageId ?? ""}`;
  };

  const handleImageDescriptionUpdate = async (imageId, newDescription) => {
    try {
      await updateItemImageDescription({
        imageId,
        description: newDescription,
      }).unwrap();
      setEditingImageId(null);
      setEditingDescription("");
      refetch();
    } catch (err) {
      console.error("Failed to update image description:", err);
      toast.error(err?.data?.error || "Failed to update image description");
    }
  };

  const handleImageReorder = async (proposalItemId, newOrder) => {
    try {
      await reorderItemImages({
        proposalItemId,
        imageOrder: newOrder,
      }).unwrap();
      console.log("Images reordered successfully");
      refetch();
    } catch (err) {
      console.error("Failed to reorder images:", err);
      toast.error(err?.data?.error || "Failed to reorder images");
    }
  };

  const handleImageDelete = async (imageId) => {
    if (window.confirm("Are you sure you want to delete this image?")) {
      try {
        await deleteItemImage(imageId).unwrap();
        refetch();
      } catch (err) {
        console.error("Failed to delete image:", err);
        toast.error(err?.data?.error || "Failed to delete image");
      }
    }
  };

  const handleImageUpload = (proposalItemId, files) => {
    const validFiles = files.filter(
      (file) => file instanceof File || file instanceof Blob
    );
    if (validFiles.length === 0) return;

    const runUpload = async () => {
      setUploadingImages((prev) => ({ ...prev, [proposalItemId]: true }));

      try {
        for (const file of validFiles) {
          const formData = new FormData();
          const filename = file instanceof File ? file.name : "image";
          formData.append("image", file, filename);
          formData.append("description", "");

          // Use axios for multipart uploads (RTK Query/fetch can fail on some servers)
          await axios.post(`proposals/items/${proposalItemId}/images`, formData);
        }

        dispatch(
          proposalsApi.util.invalidateTags([
            { type: "Proposal", id },
            { type: "ProposalItemImage" },
          ])
        );
        await refetch();
      } catch (err) {
        console.error("Failed to upload image:", err);
        const message =
          err?.response?.status === 401
            ? "Session expired. Please try uploading again."
            : err?.response?.data?.error || "Failed to upload image";
        toast.error(message);
      } finally {
        setUploadingImages((prev) => ({ ...prev, [proposalItemId]: false }));
      }
    };

    const previous =
      itemImageUploadQueueRef.current[proposalItemId] || Promise.resolve();
    const next = previous.then(runUpload, runUpload);
    itemImageUploadQueueRef.current[proposalItemId] = next;
    return next;
  };

  const handleCustomSectionImageUpload = (sectionIndex, files) => {
    const runUpload = async () => {
      const incoming = (Array.isArray(files) ? files : [files]).filter(
        (file) => file instanceof File || file instanceof Blob
      );
      if (!incoming.length) {
        toast.error("Invalid file. Please select an image file.");
        return;
      }

      const section = pdfOptionsRef.current?.customSections?.[sectionIndex];
      const maxImages = getMaxImagesForLayout(section?.layout ?? "2_1c");
      const used = section?.images?.length ?? 0;
      const remaining = Math.max(0, maxImages - used);
      if (remaining <= 0) {
        toast.error(`This layout allows at most ${maxImages} images.`);
        return;
      }

      const toUpload = incoming.slice(0, remaining);
      if (incoming.length > remaining) {
        toast.info(
          `This layout allows ${maxImages} images. ${incoming.length - remaining} extra file(s) were skipped.`
        );
      }

      try {
        setUploadingCustomSectionImages((prev) => ({
          ...prev,
          [sectionIndex]: true,
        }));

        const uploadedImages = [];
        let lastError = null;
        for (const file of toUpload) {
          try {
            const formData = new FormData();
            const filename = file instanceof File ? file.name : "image";
            formData.append("image", file, filename);
            formData.append("description", "");

            const response = await axios.post(
              "proposals/upload-custom-section-image",
              formData
            );
            const data = response.data;
            uploadedImages.push({
              url: data.url,
              description: data.description || "",
              filename: data.filename,
            });
          } catch (err) {
            lastError = err;
            if (err?.response?.status === 401) {
              break;
            }
          }
        }

        if (uploadedImages.length) {
          setPdfOptions((opts) => {
            const updatedSections = cloneCustomSections(opts.customSections);
            if (!updatedSections[sectionIndex]) {
              return opts;
            }
            const layoutMax = getMaxImagesForLayout(
              updatedSections[sectionIndex].layout ?? "2_1c"
            );
            const currentImages = updatedSections[sectionIndex].images || [];
            const space = Math.max(0, layoutMax - currentImages.length);
            updatedSections[sectionIndex] = {
              ...updatedSections[sectionIndex],
              images: [...currentImages, ...uploadedImages.slice(0, space)],
            };
            const next = {
              ...opts,
              customSections: updatedSections,
            };
            pdfOptionsRef.current = next;
            return next;
          });
          toast.success(
            uploadedImages.length === 1
              ? "Image uploaded successfully!"
              : `${uploadedImages.length} images uploaded successfully!`
          );
        }

        if (lastError) {
          const message =
            lastError?.response?.status === 401
              ? "Session expired. Please try uploading again."
              : lastError?.response?.data?.error ||
                "Failed to upload image. Please try again.";
          toast.error(message);
        }
      } catch (err) {
        console.error("Failed to upload custom section image:", err);
        toast.error("Failed to upload image. Please try again.");
      } finally {
        setUploadingCustomSectionImages((prev) => ({
          ...prev,
          [sectionIndex]: false,
        }));
      }
    };

    const previous =
      customSectionUploadQueueRef.current[sectionIndex] || Promise.resolve();
    const next = previous.then(runUpload, runUpload);
    customSectionUploadQueueRef.current[sectionIndex] = next;
    return next;
  };

  const handleDragStart = (e, imageId, proposalItemId) => {
    setDraggedImage({ imageId, proposalItemId });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", `${imageId}-${proposalItemId}`);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, targetImageId, targetProposalItemId) => {
    e.preventDefault();

    if (
      !draggedImage ||
      String(draggedImage.proposalItemId) !== String(targetProposalItemId)
    ) {
      return;
    }

    if (String(draggedImage.imageId) === String(targetImageId)) {
      return;
    }

    const item = proposal.items.find(
      (item) => String(item.proposal_item_id) === String(targetProposalItemId)
    );
    if (!item || !item.images) {
      return;
    }

    const images = [...item.images];
    const draggedIndex = images.findIndex(
      (img) => String(img.image_id) === String(draggedImage.imageId)
    );
    const targetIndex = images.findIndex(
      (img) => String(img.image_id) === String(targetImageId)
    );

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    const [removed] = images.splice(draggedIndex, 1);
    images.splice(targetIndex, 0, removed);

    const newOrder = images.map((img) => img.image_id);

    setDraggedImage(null);
    handleImageReorder(targetProposalItemId, newOrder);
  };

  const handleDragEnd = () => {
    setDraggedImage(null);
  };

  // Show loading only when proposal data is not available
  if (loading || !proposal) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <Space orientation="vertical" align="center" size="large">
          <Spin size="large" />
          <Text type="secondary">Loading proposal details...</Text>
        </Space>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error loading proposal"
        description={
          error?.data?.error || error?.message || "Failed to load proposal"
        }
        type="error"
        showIcon
        style={{ marginBottom: 24 }}
      />
    );
  }

  const handleItemsDragStart = (e, fromIndex) => {
    e.dataTransfer.setData("text/plain", String(fromIndex));
    e.dataTransfer.effectAllowed = "move";
  };

  const handleItemsDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setItemsDragOverIndex(index);
  };

  const handleItemsDragEnd = () => {
    setItemsDragOverIndex(null);
  };

  const handleItemsDrop = async (e, toIndex) => {
    e.preventDefault();
    setItemsDragOverIndex(null);
    const fromIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
    if (isNaN(fromIndex) || fromIndex === toIndex || !proposal?.items) return;
    const items = [...proposal.items];
    const [moved] = items.splice(fromIndex, 1);
    items.splice(toIndex, 0, moved);
    const itemOrder = items.map((it) => it.proposal_item_id);
    try {
      await reorderProposalItems({ proposalId: id, itemOrder }).unwrap();
      toast.success("Items reordered");
    } catch (err) {
      toast.error(err?.data?.error || "Failed to reorder items");
    }
  };

  // Table columns for proposal items
  const itemColumns = [
    {
      title: "",
      key: "drag",
      width: 40,
      render: (_, __, index) => (
        <div
          draggable
          onDragStart={(e) => handleItemsDragStart(e, index)}
          onDragEnd={handleItemsDragEnd}
          style={{ cursor: "grab" }}
          title="Drag to reorder"
        >
          <i className="bi bi-grip-vertical" style={{ fontSize: "1.1rem", color: "#999" }} />
        </div>
      ),
    },
    {
      title: "#",
      dataIndex: "index",
      key: "index",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Item",
      key: "item",
      render: (_, record) => (
        <div>
          <Space>
            <Text strong>{record.item_name}</Text>
            {(() => {
              const isCustom = record.is_custom;
              const isCustomBool =
                isCustom === 1 ||
                isCustom === "1" ||
                isCustom === true ||
                isCustom === "true";
              return isCustomBool && <Tag color="orange">Custom</Tag>;
            })()}
          </Space>
          {record.description && (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.description}
              </Text>
            </div>
          )}
          {record.custom_description && (
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <Text strong>Custom Description:</Text>{" "}
                {record.custom_description}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "right",
    },
    {
      title: "List Price",
      key: "list_price",
      width: 120,
      align: "right",
      render: (_, record) =>
        convertAndFormatPrice(
          record.list_price,
          proposal.currency_code || "CZK",
          proposal.exchange_rate_used
        ),
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      width: 100,
      align: "right",
      render: (discount) => `${discount}%`,
    },
    {
      title: "Final Price",
      key: "final_price",
      width: 120,
      align: "right",
      render: (_, record) =>
        convertAndFormatPrice(
          record.final_price,
          proposal.currency_code || "CZK",
          proposal.exchange_rate_used
        ),
    },
    {
      title: "Total",
      key: "total",
      width: 120,
      align: "right",
      render: (_, record) => (
        <Text strong>
          {convertAndFormatPrice(
            record.quantity * record.final_price,
            proposal.currency_code || "CZK",
            proposal.exchange_rate_used
          )}
        </Text>
      ),
    },
  ];

  // Expandable row render for pieces
  const expandedRowRender = (record) => {
    if (
      !record.pieces ||
      !Array.isArray(record.pieces) ||
      record.pieces.length === 0
    ) {
      return null;
    }
    return (
      <div style={{ padding: "16px", background: "#fafafa" }}>
        <PieceGrid
          pieces={record.pieces}
          title={`Pieces for ${record.item_name}`}
        />
      </div>
    );
  };

  return (
    <div style={{ maxWidth: "100%", overflow: "hidden" }}>
      {/* PDF Generation Loading Screen */}
      <PDFGenerationLoader isGenerating={isGeneratingPDF} />

      {/* Header Section */}
      <Space
        orientation="vertical"
        size="large"
        style={{ width: "100%", marginBottom: 24 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <Title level={2} style={{ margin: 0 }}>
              {proposal.name
                ? `${proposal.name} #${proposal.proposal_id}`
                : `Proposal #${proposal.proposal_id}`}
            </Title>
          </div>
          <Space>
            <Button
              icon={<CopyOutlined />}
              onClick={openDuplicateModal}
              disabled={!proposal}
            >
              Duplicate Proposal
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/proposals/${id}/edit`)}
            >
              Edit Proposal
            </Button>
          </Space>
        </div>
      </Space>

      {/* Customer Information and Proposal Information - Side by side on desktop */}
      <Row gutter={[24, 24]}>
        {/* Customer Information - Left Column */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <UserOutlined />
                <span>Customer Information</span>
                {!hasDeliveryAddress() && (
                  <Tooltip
                    title={
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>
                          Missing Delivery Address
                        </div>
                        <div>
                          This customer has no delivery address. You must add a
                          delivery address before accepting this proposal to
                          convert it to an order.
                        </div>
                      </div>
                    }
                    placement="top"
                  >
                    <ExclamationCircleOutlined
                      style={{
                        color: "#faad14",
                        fontSize: 18,
                        cursor: "help",
                      }}
                    />
                  </Tooltip>
                )}
              </Space>
            }
            style={{ height: "100%" }}
          >
            {proposal.customer ? (
              <Space
                orientation="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <div style={{ marginBottom: 8 }}>
                  <Text
                    type="secondary"
                    style={{ fontSize: 14, display: "block", marginBottom: 4 }}
                  >
                    Customer
                  </Text>
                  <Title
                    level={3}
                    style={{ margin: 0, fontSize: 24, fontWeight: 600 }}
                  >
                    {proposal.customer.first_name} {proposal.customer.last_name}
                  </Title>
                </div>
                <Descriptions column={1} size="middle">
                  <Descriptions.Item label="Email">
                    {proposal.customer.email || (
                      <Text type="secondary">Not provided</Text>
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {proposal.customer.phone || (
                      <Text type="secondary">Not provided</Text>
                    )}
                  </Descriptions.Item>
                </Descriptions>

                {proposal.customer.addresses &&
                  proposal.customer.addresses.length > 0 && (
                    <>
                      <Row gutter={[16, 16]}>
                        {proposal.customer.addresses.map((address, idx) => (
                          <Col xs={24} sm={12} key={address.address_id || idx}>
                            <Card
                              title={address.address_type_name}
                              size="small"
                              style={{ height: "100%" }}
                            >
                              <Descriptions
                                column={1}
                                size="small"
                                colon={false}
                              >
                                <Descriptions.Item
                                  label={<Text strong>Street</Text>}
                                >
                                  {address.street}
                                </Descriptions.Item>
                                {address.floor !== null &&
                                  address.floor !== undefined &&
                                  address.floor !== "" && (
                                    <Descriptions.Item
                                      label={<Text strong>Floor</Text>}
                                    >
                                      {address.floor}
                                    </Descriptions.Item>
                                  )}
                                <Descriptions.Item
                                  label={<Text strong>City</Text>}
                                >
                                  {address.city}
                                </Descriptions.Item>
                                <Descriptions.Item
                                  label={<Text strong>State/Province</Text>}
                                >
                                  {address.state_province}
                                </Descriptions.Item>
                                <Descriptions.Item
                                  label={<Text strong>Postal Code</Text>}
                                >
                                  {address.postal_code}
                                </Descriptions.Item>
                                <Descriptions.Item
                                  label={<Text strong>Country</Text>}
                                >
                                  {address.country}
                                </Descriptions.Item>
                              </Descriptions>
                            </Card>
                          </Col>
                        ))}
                      </Row>
                    </>
                  )}
              </Space>
            ) : (
              <Text type="secondary">Customer information not available</Text>
            )}
          </Card>
        </Col>

        {/* Proposal Information - Right Column */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <FileTextOutlined />
                <span>Proposal Information</span>
              </Space>
            }
            style={{ height: "100%" }}
          >
            <Space orientation="vertical" size="large" style={{ width: "100%" }}>
              {/* Core Information */}
              <Descriptions column={1} size="middle" bordered>
                <Descriptions.Item label="Status" span={1}>
                  {statusLoading ? (
                    <Spin size="small" />
                  ) : statusError ? (
                    <Tag color="error">Error loading status</Tag>
                  ) : (
                    <StatusBadge status={currentStatus?.name || "Unknown"} />
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Valid Until" span={1}>
                  <Space>
                    <CalendarOutlined />
                    <Text>{proposal.valid_until}</Text>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Total" span={1}>
                  <Text strong style={{ fontSize: 18, color: "#1890ff" }}>
                    {convertAndFormatPrice(
                      proposal.total_price,
                      proposal.currency_code || "CZK",
                      proposal.exchange_rate_used
                    )}
                  </Text>
                </Descriptions.Item>
                {proposal.exchange_rate_used &&
                  proposal.currency_code !== "CZK" && (
                    <Descriptions.Item label="Exchange Rate" span={1}>
                      <Text type="secondary">
                        1 {proposal.currency_code} ={" "}
                        {proposal.exchange_rate_used} CZK
                      </Text>
                    </Descriptions.Item>
                  )}
                <Descriptions.Item label="Currency" span={1}>
                  <Space>
                    <Text>
                      {getCurrencySymbol(proposal.currency_code || "CZK")}
                    </Text>
                    <Text>{proposal.currency_code || "CZK"}</Text>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Language" span={1}>
                  <Text>{getLanguageName(proposal.language_id)}</Text>
                </Descriptions.Item>
              </Descriptions>

              {/* Action Buttons */}
              {getNextActions().length > 0 && (
                <>
                  <Space wrap>
                    {getNextActions().map((action) => {
                      let buttonType = "default";
                      let icon = null;

                      if (action.btn === "primary") {
                        buttonType = "primary";
                        icon = <SendOutlined />;
                      } else if (action.btn === "success") {
                        buttonType = "default";
                        icon = <CheckCircleOutlined />;
                      } else if (action.btn === "warning") {
                        buttonType = "default";
                        icon = <PauseCircleOutlined />;
                      } else if (action.btn === "outline-warning") {
                        buttonType = "default";
                        icon = <CloseCircleOutlined />;
                      }

                      return (
                        <Button
                          key={action.endpoint}
                          type={buttonType}
                          danger={action.btn === "secondary" && action.disabled}
                          icon={icon}
                          onClick={() => handleStatusAction(action.endpoint)}
                          disabled={updatingStatus || action.disabled}
                          title={
                            action.disabled
                              ? "Customer must have a delivery address to accept proposal and convert to order"
                              : ""
                          }
                        >
                          {action.name}
                        </Button>
                      );
                    })}
                    {updatingStatus && (
                      <Space>
                        <Spin size="small" />
                        <Text type="secondary">Updating...</Text>
                      </Space>
                    )}
                  </Space>
                </>
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Duplicate Proposal"
        open={isDuplicateModalOpen}
        onOk={handleDuplicateProposal}
        onCancel={() => setIsDuplicateModalOpen(false)}
        confirmLoading={isDuplicating}
        okText="Duplicate"
        destroyOnHidden
      >
        <p className="text-muted">
          Create a new draft proposal for another customer using this proposal
          as the template.
        </p>

        <div className="mb-3">
          <label className="form-label">Customer</label>
          <CustomerAutocomplete
            selectedCustomerId={duplicateCustomerId}
            onSelect={(customer) =>
              setDuplicateCustomerId(customer?.person_id ?? null)
            }
            customers={[]}
            placeholder="Type to search customers..."
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Proposal Name</label>
          <Input
            value={duplicateName}
            onChange={(event) => setDuplicateName(event.target.value)}
            placeholder="Proposal name"
          />
        </div>

        <div>
          <label className="form-label">Valid Until</label>
          <input
            type="date"
            className="form-control"
            value={duplicateValidUntil}
            onChange={(event) => setDuplicateValidUntil(event.target.value)}
            min={new Date().toISOString().split("T")[0]}
          />
        </div>
      </Modal>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={24}>
          <Card
            title={
              <Space>
                <FileTextOutlined />
                <span>PDF Generation</span>
              </Space>
            }
            style={{ marginTop: 24, marginBottom: 24 }}
          >
            <PDFTemplateOptions
              selectedTemplate={selectedTemplate}
              setSelectedTemplate={setSelectedTemplate}
              templateOptions={templateOptions}
              pdfOptions={pdfOptions}
              setPdfOptions={setPdfOptions}
              proposal={proposal}
              uploadingImages={uploadingImages}
              handleImageUpload={handleImageUpload}
              draggedImage={draggedImage}
              handleDragStart={handleDragStart}
              handleDragOver={handleDragOver}
              handleDrop={handleDrop}
              handleDragEnd={handleDragEnd}
              editingImageId={editingImageId}
              editingDescription={editingDescription}
              setEditingImageId={setEditingImageId}
              setEditingDescription={setEditingDescription}
              handleImageDescriptionUpdate={handleImageDescriptionUpdate}
              handleCustomSectionImageUpload={handleCustomSectionImageUpload}
              uploadingCustomSectionImages={uploadingCustomSectionImages}
              handleImageDelete={handleImageDelete}
              handleDownloadPDF={handleDownloadPDF}
            />

            {/* HTML Preview Section - Only show when debug features are enabled */}
            {debugFeatures?.enabled && (
              <Card
                title="HTML Preview (Developer Tool)"
                style={{ marginTop: 24 }}
                extra={
                  <Space>
                    <Button
                      size="small"
                      onClick={() => setShowPreview(!showPreview)}
                    >
                      {showPreview ? "Hide Preview" : "Show Preview"}
                    </Button>
                    {showPreview && (
                      <Button
                        size="small"
                        type="primary"
                        onClick={refreshPreview}
                      >
                        Refresh Preview
                      </Button>
                    )}
                  </Space>
                }
              >
                {showPreview && (
                  <Space
                    orientation="vertical"
                    style={{ width: "100%" }}
                    size="middle"
                  >
                    <Alert
                      message="Developer Tool"
                      description="This preview shows the exact HTML that will be used for PDF generation. Use browser developer tools to inspect and modify styling in real-time."
                      type="info"
                      showIcon
                    />
                    <ProposalPreview
                      proposalId={id}
                      templateType={selectedTemplate}
                      pdfOptions={pdfOptions}
                      onRefresh={setRefreshPreview}
                    />
                  </Space>
                )}
              </Card>
            )}

            {/* Pretty Proposal PDF Options - Only show when pretty_proposal template is selected */}
            {selectedTemplate === "pretty_proposal" && (
              <CleanProposalPDFOptions
                proposal={proposal}
                pdfOptions={pdfOptions}
                setPdfOptions={setPdfOptions}
                uploadingImages={uploadingImages}
                handleImageUpload={handleImageUpload}
                editingImageId={editingImageId}
                editingDescription={editingDescription}
                setEditingImageId={setEditingImageId}
                setEditingDescription={setEditingDescription}
                handleImageDescriptionUpdate={handleImageDescriptionUpdate}
                handleImageDelete={handleImageDelete}
                handleDownloadPDF={handleDownloadPDF}
                templateType="pretty_proposal"
              />
            )}
          </Card>
        </Col>
      </Row>
      {/* PDF Generation - Separate Card */}

      {/* Proposal Items */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>Proposal Items</span>
            {proposal.items && Array.isArray(proposal.items) && (
              <Badge
                count={proposal.items.length}
                style={{ backgroundColor: "#52c41a" }}
              />
            )}
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        {proposal.items &&
        Array.isArray(proposal.items) &&
        proposal.items.length > 0 ? (
          <Table
            columns={itemColumns}
            dataSource={proposal.items.map((item, index) => ({
              ...item,
              index,
              key: item.proposal_item_id || item.id || index,
            }))}
            pagination={false}
            onRow={(record, index) => ({
              onDragOver: (e) => handleItemsDragOver(e, index),
              onDrop: (e) => handleItemsDrop(e, index),
              style:
                itemsDragOverIndex === index
                  ? {
                      backgroundColor: "rgba(24, 144, 255, 0.08)",
                      outline: "2px dashed #1890ff",
                      outlineOffset: "-2px",
                    }
                  : undefined,
            })}
            expandable={{
              expandedRowRender,
              rowExpandable: (record) =>
                record.pieces &&
                Array.isArray(record.pieces) &&
                record.pieces.length > 0,
            }}
            size="middle"
          />
        ) : (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Text type="secondary">No items in this proposal</Text>
          </div>
        )}
      </Card>

      {/* Voice Notes */}
      <EntityNotes entityType="proposal" entityId={proposal.proposal_id} />

      {/* PDF Version History */}
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>PDF Version History</span>
          </Space>
        }
      >
        <PdfSnapshotsTable proposalId={proposal.proposal_id} />
      </Card>
    </div>
  );
};

export default ProposalDetailsPage;
